import React, { useState, useEffect, useRef } from 'react';

const Chat = ({ jobId, currentUserId, currentUserName, userRole }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/${jobId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/jobs/${jobId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sender_id: currentUserId,
          content: newMessage
        })
      });
      const data = await response.json();
      if (data.success) {
        setNewMessage('');
        fetchMessages();
      }
    } catch (err) {
      console.error('Error sending message:', err);
    }
    setSending(false);
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [jobId]);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>Loading chat...</div>;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '460px',
      backgroundColor: '#0f172a',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
      border: '1px solid #334155'
    }}>
      {/* Chat Header – centered */}
      <div style={{
        padding: '14px 20px',
        background: 'linear-gradient(135deg, #1e293b, #0f172a)',
        borderBottom: '2px solid #eab308',
        color: '#eab308',
        fontWeight: 'bold',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px'
      }}>
        <span style={{ fontSize: '22px' }}>💬</span>
        <span>Chat with {userRole === 'driver' ? 'Customer' : 'Driver'}</span>
      </div>

      {/* Messages Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        background: 'radial-gradient(circle at 10% 20%, #111827, #0f172a)'
      }}>
        {messages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#94a3b8',
            padding: '40px 20px',
            fontSize: '14px',
            fontStyle: 'italic'
          }}>
            ✨ No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isCurrentUser = msg.sender_id === currentUserId;
            const senderName = msg.sender_name || (isCurrentUser ? currentUserName : 'Other');
            const initial = senderName.charAt(0).toUpperCase();
            const randomColor = isCurrentUser ? '#eab308' : '#3b82f6';
            const bubbleBg = isCurrentUser ? 'linear-gradient(135deg, #eab308, #fbbf24)' : 'linear-gradient(135deg, #1e293b, #334155)';
            const textColor = isCurrentUser ? '#1e1e1e' : '#f1f5f9';
            
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: isCurrentUser ? randomColor : '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  color: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  border: `1px solid ${isCurrentUser ? '#eab308' : '#3b82f6'}`
                }}>
                  {initial}
                </div>
                
                {/* Message Bubble */}
                <div style={{
                  maxWidth: '70%',
                  background: bubbleBg,
                  borderRadius: '18px',
                  padding: '10px 16px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  borderBottomRightRadius: isCurrentUser ? '4px' : '18px',
                  borderBottomLeftRadius: !isCurrentUser ? '4px' : '18px'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    marginBottom: '4px',
                    color: isCurrentUser ? '#4a1f00' : '#eab308'
                  }}>
                    {senderName}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    wordWrap: 'break-word',
                    color: textColor,
                    lineHeight: '1.4'
                  }}>
                    {msg.content}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    marginTop: '6px',
                    opacity: 0.7,
                    textAlign: 'right',
                    color: textColor
                  }}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: '#111827',
        borderTop: '1px solid #334155',
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="Type your message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: '30px',
            border: '1px solid #334155',
            backgroundColor: '#1e293b',
            color: '#f1f5f9',
            fontSize: '14px',
            outline: 'none',
            transition: 'all 0.2s',
            fontFamily: 'inherit'
          }}
          onFocus={(e) => e.target.style.borderColor = '#eab308'}
          onBlur={(e) => e.target.style.borderColor = '#334155'}
        />
        <button
          onClick={sendMessage}
          disabled={sending || !newMessage.trim()}
          style={{
            padding: '10px 24px',
            background: newMessage.trim() ? 'linear-gradient(135deg, #eab308, #fbbf24)' : '#475569',
            color: 'black',
            border: 'none',
            borderRadius: '40px',
            cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
            fontWeight: 'bold',
            fontSize: '14px',
            transition: 'transform 0.1s',
            boxShadow: newMessage.trim() ? '0 2px 6px rgba(0,0,0,0.2)' : 'none'
          }}
          onMouseDown={(e) => { if (newMessage.trim()) e.currentTarget.style.transform = 'scale(0.96)' }}
          onMouseUp={(e) => { if (newMessage.trim()) e.currentTarget.style.transform = 'scale(1)' }}
        >
          {sending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default Chat;
